import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { ReactComponent as IconClose } from '../../../images/icons/icon_close.svg'
import { ReactComponent as IconGoogleAds } from '../../../images/vendors/google_ads_logo.svg'
import { ReactComponent as IconMetaAds } from '../../../images/vendors/meta_logo.svg'
import { ReactComponent as IconTikTok } from '../../../images/vendors/tiktok_logo.svg'
import { useNavigate } from 'react-router'
import { Button, CardRadioInput } from '@demandlocal/components'
import { useGetLinkedAccounts } from '../../../api/agencies/useGetLinkedAccounts'
import { VENDOR } from '../../../lib/utils/vendors'
import { match } from 'ts-pattern'
import { Vendor } from '../../../lib/types/Vendor'
import { useDealership } from '../../../store/zustand/dealershipStore'
import InfoMessage from '../../../components/InfoMessage'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type FieldValues = {
  vendor: 'google-ads' | 'facebook-ads' | 'tiktok-ads'
}

export default function CampaignImportTypeForm(): JSX.Element {
  const navigate = useNavigate()
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const formContext = useForm<FieldValues>({})
  const { data, isLoading: isLoadingLinkedAccounts } = useGetLinkedAccounts()
  const googleAccount = data?.find((account) => account.id === VENDOR.Google)
  const metaAccounts = data?.find((account) => account.id === VENDOR.Facebook)
  const {
    handleSubmit,
    formState: { isDirty },
  } = formContext

  const onSubmit = (values: FieldValues): void => {
    const { vendor } = values
    navigate(`./${vendor}`)
  }

  const getVendorDescription = (
    id: number,
    vendor?: Vendor,
    isLoading?: boolean,
  ): JSX.Element => {
    return match(id)
      .with(VENDOR.Google, () => {
        return isLoading ? (
          <span>Loading...</span>
        ) : vendor?.connected ? (
          <span>Import campaigns from your Google Ads account</span>
        ) : (
          <div>
            Connect your Google Ads account in "
            <Link
              className={'font-bold text-primary-persian-blue'}
              to={`/dashboard/agency/${dealership?.organization_id}/dealership/${dealership?.id}/accounts`}>
              Linked Platforms
            </Link>
            " section first.
          </div>
        )
      })
      .with(VENDOR.Facebook, () => {
        return isLoading ? (
          <span>Loading...</span>
        ) : vendor?.connected ? (
          <span>Import campaigns from your Facebook Ads account</span>
        ) : (
          <div>
            Connect your Meta Ads account in "
            <Link
              className={'font-bold text-primary-persian-blue'}
              to={`/dashboard/agency/${dealership?.organization_id}/dealership/${dealership?.id}/accounts`}>
              Linked Platforms
            </Link>
            " section first.
          </div>
        )
      })
      .otherwise(() => <span>'Coming soon'</span>)
  }

  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-1 flex-col h-screen'}>
        <div className={'py-5 flex justify-between items-center px-12'}>
          <h1 className={' text-3xl font-bold'}>Import Campaigns</h1>
          <IconClose
            className={'cursor-pointer'}
            onClick={(e) => {
              navigate('../../../')
            }}
          />
        </div>
        <div className="flex flex-row items-center px-12 gap-5">
          <span className={'text-primary-xiketic text-base pb-2 flex-wrap'}>
            Import campaigns from your Google Ads or Facebook Ads
          </span>
        </div>
        <div className={'h-full overflow-auto pt-10 flex flex-col gap-10'}>
          <div className={'flex-1 flex-col flex justify-center px-12 gap-5'}>
            <div className={'grid grid-cols-2 gap-3'}>
              <CardRadioInput
                disabled={isLoadingLinkedAccounts || googleAccount?.connected === false}
                icon={<IconGoogleAds className={'w-24'} />}
                name={'vendor'}
                value={'google-ads'}
                description={getVendorDescription(
                  VENDOR.Google,
                  googleAccount,
                  isLoadingLinkedAccounts,
                )}
                background={'white'}
              />
              <CardRadioInput
                disabled={isLoadingLinkedAccounts || metaAccounts?.connected === false}
                icon={<IconMetaAds className={'w-32 h-24'} />}
                name={'vendor'}
                value={'facebook-ads'}
                description={getVendorDescription(
                  VENDOR.Facebook,
                  metaAccounts,
                  isLoadingLinkedAccounts,
                )}
                background={'white'}
              />
            </div>
            <InfoMessage
              content={
                <div className="flex flex-row justify-between w-full items-center">
                  <div className="flex flex-col gap-3">
                    <h3 className={'font-bold text-lg text-primary-xiketic'}>
                      Don't want to do it yourself? Let us market your audience
                    </h3>
                    <h4 className={'font-medium text-base text-davy-gray'}>
                      Grow your audience, market your cars, create highly effective
                      targeted ads.
                    </h4>
                  </div>
                  <Button
                    onClick={(e) => {
                      e.preventDefault()
                      navigate('../../new')
                    }}
                    variant={'rainbow'}>
                    Let's get started
                  </Button>
                </div>
              }
            />
          </div>
        </div>

        <div className={'flex justify-between py-5 border-t px-12 mt-'}>
          <Button
            variant={'tertiary'}
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.campaigns.create', { dealership }))
            }}>
            Back
          </Button>
          <Button disabled={!isDirty} variant={'primary'}>
            Continue
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
